import React, { useCallback, useEffect, useState, useRef } from "react";
import { Box, Text } from "@mantine/core";
import { Dropzone, FileWithPath } from "@mantine/dropzone";
import { IconUpload, IconX } from "@tabler/icons-react";
import { TSubjectId, TCompanyId } from "@/common/types";
import { TProtocolId } from "@/protocols/types/protocol.ts";
import {
  useProtocolStore
} from "../../store/protocols";
import classes from "./ProtocolFileManager.module.css";
import { IProtocolRawFile } from "@/protocols/types/IProtocolRawFile.ts";
import { useUnifiedFiles } from "../hooks/useUnifiedFiles";
import { useUploadStatusStore } from "@/protocols/persistence/useUploadStatusStore";
import { TWorkflow } from "@/protocols/common/steps/StepView.tsx";
import { FileStatusTable } from "@/protocols/common/components/FileStatusTable.tsx";
import { modals } from "@mantine/modals";
import { extractOriginalFilename } from "@/common/utils/fileUtils.ts";
import { isTauriAvailable } from "@/common/utils/tauri.ts";
import { getUploadStore } from "@/protocols/persistence/uploadStore";
import { showErrorNotification } from "@/common/notifications";
import { TauriDragDropZone } from "./TauriDragDropZone";

const NATIVE_PICK_EXTENSIONS: string[] = [];

interface ProtocolFileManagerProps {
  companyId: TCompanyId;
  protocolId: TProtocolId;
  subjectId?: TSubjectId;
  workflow: TWorkflow
  /**
   * Optional callback fired whenever the server-side file count
   * changes (mount, after a successful refresh, after a delete).
   * Lets parent components like StopDataCollectionStepView render
   * an accurate "files on the server" count without making a
   * second loadProtocolFiles request.
   */
  onFileCountChange?: (count: number) => void
}

export const ProtocolFileManager: React.FC<ProtocolFileManagerProps> = ({
  companyId,
  protocolId,
  subjectId,
  workflow,
  onFileCountChange,
}) => {
  const uploadedFiles = useUploadStatusStore((s) => s.serverFiles);
  const [isLoading, setIsLoading] = useState(true);
  const addProtocolFiles = useProtocolStore((state) => state.addProtocolFiles);
  const deleteProtocolFile = useProtocolStore((state) => state.deleteProtocolFile);

  useEffect(() => {
    setIsLoading(false)
  }, [uploadedFiles])

  // Notify parent when the file count changes (mount, refresh,
  // post-delete). Lets e.g. StopDataCollectionStepView render an
  // accurate "files on the server" badge without a separate
  // loadProtocolFiles request — see the comment on
  // `onFileCountChange` above.
  useEffect(() => {
    if (workflow === "planning") return
    onFileCountChange?.(uploadedFiles.length)
  }, [uploadedFiles.length, onFileCountChange, workflow])

  const pendingUploadsCount = useUploadStatusStore((s) => s.pendingUploads.length);
  const prevPendingCountRef = useRef<number | null>(null);

  useEffect(() => {
    // we are loading now until the serverFiles refreshes
    const prev = prevPendingCountRef.current
    prevPendingCountRef.current = pendingUploadsCount
    if (prev !== null && prev > pendingUploadsCount) {
      setIsLoading(true);
    }
  }, [pendingUploadsCount, companyId, protocolId]);

  // Browser mode (HTML5 drag/drop): in-flight XHR uploads show in
  // the slice. Refresh the file list when the last one completes.
  const browserUploadsCount = useUploadStatusStore((s) => s.browserUploads.size);
  const prevBrowserUploadsCountRef = useRef<number | null>(null);

  useEffect(() => {
    if (prevBrowserUploadsCountRef.current !== null
      && prevBrowserUploadsCountRef.current > 0
      && browserUploadsCount === 0) {
      // we are loading now until the serverFiles refreshes
      setIsLoading(true)
    }
    prevBrowserUploadsCountRef.current = browserUploadsCount;
  }, [browserUploadsCount, companyId, protocolId]);

  const unifiedFiles = useUnifiedFiles({
    protocolId,
    uploadedFiles,
  });

  const handleFileSelected = (files: Array<FileWithPath>) => {
    // Browser mode only (Mantine Dropzone wires this to its onDrop).
    if (subjectId) {
      addProtocolFiles(companyId, subjectId, protocolId, files);
    } else {
      console.error("Cannot add files: subjectId is missing");
    }
  };

  const inTauri = isTauriAvailable();
  const fileActionDisabled = workflow !== "session" || !subjectId;

  // Tauri-mode shared handler: when a file path comes back from
  // either the native picker or the native drag/drop, hand it to
  // the active store's pick / drop method. Both paths insert a
  // 'pending' row directly in pending_uploads; the worker then
  // streams the local copy to S3 and the polling-driven
  // pendingUploads effect refreshes the file list once the count
  // returns to zero.
  const handleSinglePath = useCallback(async (sourcePath: string) => {
    const store = getUploadStore()
    if (!store.enqueuePickedFile) return
    try {
      await store.enqueuePickedFile({
        companyId, protocolId, sourcePath,
        protocolStepId: undefined, // intentional as we don't want it associated to step only protocol
      })
    } catch (e: unknown) {
      const message = e instanceof Error ? e.message : String(e)
      showErrorNotification({ title: "Could not pick file", message })
    }
  }, [companyId, protocolId])

  const handleNativePick = useCallback(async () => {
    if (!subjectId) return
    const store = getUploadStore()
    if (!store.pickFile) return
    try {
      const sourcePath = await store.pickFile({
        allowedExtensions: [...NATIVE_PICK_EXTENSIONS],
      })
      if (!sourcePath) return
      await handleSinglePath(sourcePath)
    } catch (e: unknown) {
      const message = e instanceof Error ? e.message : String(e)
      showErrorNotification({ title: "Could not pick file", message })
    }
  }, [subjectId, handleSinglePath])

  const handleFilesDropped = useCallback(async (paths: string[]) => {
    if (!subjectId) return
    const store = getUploadStore()
    if (!store.enqueuePickedFiles) return
    try {
      await store.enqueuePickedFiles(
        paths.map((sourcePath) => ({ companyId, protocolId, sourcePath }))
      )
    } catch (e: unknown) {
      const message = e instanceof Error ? e.message : String(e)
      showErrorNotification({ title: "Could not add dropped files", message })
    }
  }, [companyId, protocolId, subjectId])

  const handleDelete = async (file: IProtocolRawFile) => {
    modals.openConfirmModal({
      title: (<Text>
        Delete file <Text span fw={700}>{extractOriginalFilename(file.id, file.filename)}</Text>?
      </Text>),
      children: (
        <Box>
          <Text span fw={700}>
            This action is <Text span fw={900} fs={"italic"}>immediate </Text>and cannot be undone.
          </Text>
        </Box>
      ),
      size: "lg",
      labels: { confirm: "Delete", cancel: "Cancel" },
      confirmProps: { color: "red" },
      onConfirm: async () => {
        // on failure disable the loading since the serverFiles might never be refreshed
        await deleteProtocolFile(companyId, protocolId, file.id).catch(() => setIsLoading(false));
        // we are loading now until the serverFiles refreshes
        setIsLoading(true)
      }
    });
  };

  if (workflow === "planning") {
    return (
      <Box p="sm">
        <Text size="sm" c="dimmed" ta="center">
          Files will be collected during protocol execution
        </Text>
      </Box>
    );
  }

  return (
    <Box className={classes.container}>
      {inTauri ? (
        // Tauri mode: the OS-level dragDrop is captured by
        // Tauri (dragDropEnabled=true in tauri.conf.json) and
        // delivered to TauriDragDropZone via onDragDropEvent.
        // The webview HTML dropzone is bypassed entirely; the
        // webview's <input type="file"> is irrelevant here
        // because the user already clicks the native picker
        // via handleNativePick.
        <TauriDragDropZone
          disabled={fileActionDisabled}
          onFilesDropped={handleFilesDropped}
          onPickClicked={handleNativePick}
        />
      ) : (
        // Browser mode: Mantine Dropzone handles HTML5 drag/drop
        // and the click-to-open HTML <input type="file">. The
        // resulting File objects have no absolute path; the
        // coordinator uses uploadFileInBrowser -> the XHR PUT
        // to S3 with onUploadProgress driving the slice entry.
        <Dropzone
          onDrop={handleFileSelected}
          multiple={true}
          disabled={fileActionDisabled}
          activateOnClick={true}
          className={classes.dropzone}
        >
          <Box className={classes.dropzoneContent}>
            <Dropzone.Accept>
              <Box style={{ display: "grid", alignItems: "center", justifyItems: "center", gap: "0.25rem" }}>
                <IconUpload size={"1.5em"} color={"var(--mantine-color-green-6)"} />
                <Text size="xs" c="green">Drop files here</Text>
              </Box>
            </Dropzone.Accept>
            <Dropzone.Reject>
              <IconX size={"1.5em"} color={"var(--mantine-color-red-6)"} />
            </Dropzone.Reject>
            <Dropzone.Idle>
              <IconUpload size={"1.5em"} color={"var(--mantine-color-dimmed)"} />
              <Text size="xs" c="dimmed">Add files</Text>
            </Dropzone.Idle>
          </Box>
        </Dropzone>
      )}

      {<FileStatusTable unifiedFiles={unifiedFiles} onDelete={handleDelete} workflow={workflow} loading={isLoading} />}
    </Box>
  );
};
